import { AppConfig } from '../app/AppConfig';
import { Database } from './database';
import { Server } from './server';
import { Service, ServiceResponse } from './service';

export class UserService extends Service {
  protected user: any;

  constructor() {
    super();
    this.user = null;
    this.onFriendConnect = this.onFriendConnect.bind(this);
    this.onFriendDisconnect = this.onFriendDisconnect.bind(this);
    this.onFriendPresence = this.onFriendPresence.bind(this);
    this.onFriendInvited = this.onFriendInvited.bind(this);
    this.onFriendAccepted = this.onFriendAccepted.bind(this);
    this.onFriendDeclined = this.onFriendDeclined.bind(this);
    this.onFriendRemoved = this.onFriendRemoved.bind(this);
  }

  public async init(): Promise<ServiceResponse> {
    Server.network.addEventListener('friend-connect', this.onFriendConnect);
    Server.network.addEventListener('friend-disconnect', this.onFriendDisconnect);
    Server.network.addEventListener('friend-presence', this.onFriendPresence);
    Server.network.addEventListener('friend-invited', this.onFriendInvited);
    Server.network.addEventListener('friend-accepted', this.onFriendAccepted);
    Server.network.addEventListener('friend-declined', this.onFriendDeclined);
    Server.network.addEventListener('friend-removed', this.onFriendRemoved);
    return {success: true};
  }
  
  public async sync(): Promise<ServiceResponse> {
    let response = await this.sendCommand('user', 'get-user');

    if(response.status == 404) {
      await this.sendCommand('user', 'create-user');
      response = await this.sendCommand('user', 'get-user');
    }
    
    if(response.status != 200)
      return {success: false};

    this.user = response.body.user;

    for(let i = 0; i < this.user.assets.length; i++) {
      let userAsset = this.user.assets[i];
      let asset = Database.getAsset(userAsset.id);
      this.user.assets[i] = { ...userAsset, ...asset };
    }

    let now = new Date();
    this.user.presence = {online: true, location: 'site', date: now.toISOString()};

    // cache profiles
    Server.public.addProfileToCache(this.user);

    let friendIds = [];
    for (let i = 0; i < this.user.friends.length; i++) 
      friendIds.push(this.user.friends[i].id);
    
    await Server.public.loadProfiles(friendIds);

    // friend presence
    for(let i = 0; i < this.user.friends.length; i++) 
      this.user.friends[i].presence = {online: false};

    response = await this.sendCommand('user', 'get-friends-online');

    if(response.status == 200) {
      for(let i = 0; i < response.body.friends.length; i++) {
        let onlineFriend = response.body.friends[i];
        let friend = this.getFriend(onlineFriend.id);
        friend.presence = {
          date: onlineFriend.date,
          location: onlineFriend.location,
          online: true
        }
      }
    }

    setTimeout(() => {
      for(let i = 0; i < this.user.friends.length; i++) {
        let state = this.user.friends[i].state; 
        if(state == 'invited' || state == 'declined')
          this.notifyListeners('friend-updated', {friend: this.user.friends[i].id, action: state})
      }
    }, 3000);

    return {success: true};
  }

  public async syncAsset(id:string) {
    let response = await this.sendCommand('user', 'get-asset', {id});
    if(response.status != 200) 
        return {success: false, message: response.body.message};

    let asset = this.getAsset(id);
    let serverAsset = response.body.asset;

    if(!serverAsset && asset) {
      let i = this.user.assets.indexOf(asset);
      this.user.assets.splice(i, 1);
    }
    else if(serverAsset && !asset) {
      let dbAsset = Database.getAsset(serverAsset.id);
      let newAsset = { ...serverAsset, ...dbAsset };
      this.user.assets.push(newAsset);
    }
    else if(serverAsset && asset)
      asset.quantity = serverAsset.quantity;

    return {success: true}
  }

  public clear() {
    this.user = null;
  }

  // profile
  public getId(): string {
    if(!this.user)
      return '';
    return this.user.id;
  }

  public isCurrent(id:string):boolean {
    return this.user.id == id;
  }

  public getName(): string {
    if(!this.user)
      return '';
    return this.user.name;
  }

  public getSlug(): string {
    if(!this.user)
      return '';
    return this.user.slug;
  }

  public getBanner(): string {
    if(!this.user)
      return '';
    return this.user.banner;
  }

  public getPortrait(): string {
    if(!this.user)
      return '';
    return this.user.portrait;
  }

  public getBio(): string {
    if(!this.user)
      return '';
    return this.user.bio;
  }

  public getFollowersCount(): number {
    if(!this.user) return 0;
    return this.user.followers;
  }

  public getFollowingCount(): number {
    if(!this.user) return 0;
    return this.user.following;
  }

  public getProfile():any {
    if(!this.user)
      return null;
      
    return {
      id: this.user.id,
      name: this.user.name,
      slug: this.user.slug,
      banner: this.user.banner,
      portrait: this.user.portrait,
      bio: this.user.bio
    }
  }

  public async setName(name:string) {
    let response = await this.sendCommand('user', 'set-name', {name: name});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.name = name;

    this.notifyListeners('user-profile-updated');

    return {success: true};
  }

  public async setSlug(slug:string) {
    let response = await this.sendCommand('user', 'set-slug', {slug});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.slug = slug;

    return {success: true};
  }

  public async setPortrait(portrait:string) {
    let response = await this.sendCommand('user', 'set-portrait', {portrait});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.portrait = portrait;

    this.notifyListeners('user-profile-updated');

    return {success: true};
  }

  public async setBanner(banner:string) {
    let response = await this.sendCommand('user', 'set-banner', {banner});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.banner = banner;

    return {success: true};
  }

  public async setBio(bio:string) {
    let response = await this.sendCommand('user', 'set-bio', {bio});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.bio = bio;

    return {success: true};
  }

  public async setProfile(slug:string, name:string, bio:string, banner:string, portrait:string) {
    let response = await this.sendCommand('user', 'set-profile', {slug, name, bio, banner, portrait});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.name = name;
    this.user.bio = bio;
    this.user.portrait = portrait;
    this.user.banner = banner;

    this.notifyListeners('user-profile-updated');

    return {success: true};
  }

  public async welcome(name:string, banner:string, portrait:string) {
    let response = await this.sendCommand('user', 'welcome', {name, banner, portrait});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.name = name;
    this.user.portrait = portrait;
    this.user.banner = banner;

    this.addAssetsToCache(portrait, 1);
    this.addAssetsToCache(banner, 1);

    this.notifyListeners('user-profile-updated');

    return {success: true};
  }

  // assets
  public getTokenCount(): number {
    let asset = this.getAsset(AppConfig.token.id);
    if(!asset)
      return 0;

    return asset.quantity;
  }

  public getCoinCount(): number {
    let asset = this.getAsset(AppConfig.coin.id);
    if(!asset)
      return 0;

    return asset.quantity;
  }
  
  public getAssets(): any[] {
    return this.user ? this.user.assets : [];
  }

  protected sortAssets(assets:any[]) {
    assets.sort((a, b)=>{
      if(a.category > b.category)
        return 1;
      else if(a.category < b.category)
        return -1;

      if(a.type > b.type)
        return 1;
      else if(a.type < b.type)
        return -1;

      if(a.price > b.price)
        return 1;
      else if(a.price < b.price)
        return -1;

      if(a.name > b.name)
        return 1;
      else if(a.name < b.name)
        return -1;

      return 0;
    });
  }

  public getAssetsByCategory(category:string): any[] {
    if(!this.user)
      return [];

    let assets = [];
    for(let i = 0; i < this.user.assets.length; i++)
      if(this.user.assets[i].category == category || category == 'all')
        assets.push(this.user.assets[i])

    this.sortAssets(assets);
    
    return assets;
  }

  public getAssetsByType(type:string): any[] {
    if(!this.user)
      return [];

    let assets = [];
    for(let i = 0; i < this.user.assets.length; i++)
      if(this.user.assets[i].type == type || type == 'all')
        assets.push(this.user.assets[i])

    this.sortAssets(assets);

    return assets;
  }

  public getAsset(id:string): any {
    if(!this.user)
      return null;

    let assets = this.user.assets;
    if(assets.length == 0)
      return null;

    for(let i = 0; i < assets.length; i++) 
      if(assets[i].id == id)
        return assets[i];

    return null;
  }

  public getAssetQuantity(id:string): number {
    let asset = this.getAsset(id);
    if(!asset)
      return 0;
    return asset.quantity;
  }

  public addAssetsToCache(id:string, quantity:number) {
    let asset = this.getAsset(id);

    if(!asset) {
      let dbAsset = Database.getAsset(id);
      let newAsset = { ...{id, quantity}, ...dbAsset };
      this.user.assets.push(newAsset);
    }
    else
      asset.quantity += quantity;
  }

  public removeAssetsFromCache(id:string, quantity:number) {
    let asset = this.getAsset(id);

    if(!asset) 
      return;

    asset.quantity -= quantity;

    if(asset.quantity <= 0) {
      let i = this.user.assets.indexOf(asset);
      this.user.assets.splice(i, 1);
    }
  }


  public async consumeAsset(id:string, quantity:number) {
    let asset = this.getAsset(id);

    if(!asset)
      return {success: false, message: 'Asset not found!'};

    if(asset.quantity < quantity)
      return {success: false, message: 'Not enough to burn!'};

    let response = await this.sendCommand('user', 'consume-asset', {id, quantity});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.removeAssetsFromCache(id, quantity);

    return {success: true};
  }

  public async purchaseAsset(id:string, quantity:number, currency:string='') {
    let shopAsset = Server.shop.getAsset(id);

    if(!shopAsset)
      return {success: false, message: 'Shop asset not found!'};

    if(currency == '')
      currency = shopAsset.currency;

    let validCurrency = (currency == shopAsset.currency);
    if(shopAsset.currency == AppConfig.token.id || shopAsset.currency == 'usd')
      validCurrency = (currency == AppConfig.token.id || currency == 'usd');

    if(!validCurrency) 
      return {success: false, message: 'Invalid currency specified!'};

    if(currency != 'usd') {
      let currencyAsset = this.getAsset(currency);
      if(!currencyAsset || currencyAsset.quantity < shopAsset.price)
        return {success: false, message: 'You do not have enough to purchase this item!'};
    }

    let response = await this.sendCommand('shop', 'purchase-asset', {id, quantity, currency});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    if(response.body.spent.currency == 'usd') {
      let cookie = this.getCookie('DollarsSpent');
      let dollarsSpent = cookie ? +cookie : 0;
      dollarsSpent += response.body.spent.price;
      this.setCookie('DollarsSpent', dollarsSpent.toString())
    }
    else {
      this.removeAssetsFromCache(response.body.spent.currency, response.body.spent.price);
    }

    this.addAssetsToCache(id, quantity);

    return {success: true};
  }

  public async reset() {
    await this.sendCommand('user', 'reset-user');
    await this.sync();
    return {success: true};
  }

  public async openPack(id:string) {
    let packAsset = this.getAsset(id);

    if(!packAsset)
      return {success: false, message: 'Pack not found!'};

    let response = await this.sendCommand('user', 'open-pack', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    for(let i = 0; i < response.body.assets.length; i++) 
      this.addAssetsToCache(response.body.assets[i].id, response.body.assets[i].quantity);

    this.removeAssetsFromCache(id, 1);

    return {success: true, assets: response.body.assets};
  }

  public async purchaseTokens(quantity:number, currency:string='') {
    if(currency != 'coin' && currency != 'usd')
      return {success: false, message: 'Invalid currency specified!'};

    let coinsPerToken = 10;
    let tokensPerDollar = 100;
    let price = 0;

    if(currency == 'coin')
      price = quantity * coinsPerToken;
    else
      price = quantity / tokensPerDollar

    if(currency == 'coin') {
      let currencyAsset = this.getAsset(currency);
      if(!currencyAsset || currencyAsset.quantity < price)
        return {success: false, message: 'You do not have enough to purchase this item!'};
    }

    let response = await this.sendCommand('user', 'purchase-tokens', {quantity, currency});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    if(currency == 'usd') {
      let cookie = this.getCookie('DollarsSpent');
      let dollarsSpent = cookie ? +cookie : 0;
      dollarsSpent += price;
      this.setCookie('DollarsSpent', dollarsSpent.toFixed(2))
    }
    else {
      this.removeAssetsFromCache(currency, price);
    }

    this.addAssetsToCache(AppConfig.token.id, quantity);

    return {success: true};
  }

  public async transferTokens(friend:string, quantity:number) {
    let response = await this.sendCommand('user', 'transfer-tokens', {friend, quantity});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.removeAssetsFromCache(AppConfig.token.id, quantity);

    // Server.network.sendCommand('accept-friend', {friend: id});

    return {success: true};
  }

  public async sellTokens(quantity:number) {
    let tokensPerDollar = 100;

    let currencyAsset = this.getAsset(AppConfig.token.id);
    if(!currencyAsset || currencyAsset.quantity < quantity)
      return {success: false, message: `You do not have ${quantity} tokens.`};

    let response = await this.sendCommand('user', 'sell-tokens', {quantity});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.removeAssetsFromCache(AppConfig.token.id, quantity);

    let tokenPrice = 1 / tokensPerDollar;
    let fees = Math.floor(quantity * 0.06);
    let total = (quantity - fees) * tokenPrice;

    let cookie = this.getCookie('DollarsSpent');
    let dollarsSpent = cookie ? +cookie : 0;
    dollarsSpent -= total;
    this.setCookie('DollarsSpent', dollarsSpent.toFixed(2))

    return {success: true};
  }

  public async craftAsset(input:any[], output:any[]) {
    let response = await this.sendCommand('user', 'craft-asset', {input, output});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    for(let i = 0; i < input.length; i++)
      this.removeAssetsFromCache(input[i].id, input[i].quantity);

    for(let i = 0; i < output.length; i++)
      this.addAssetsToCache(output[i].id, output[i].quantity);

    return {success: true};
  }

  // cookies
  public findCookie(key:string) {
    for(let i = 0; i < this.user.cookies.length; i++)
      if(this.user.cookies[i].key == key)
        return this.user.cookies[i];
    return null;
  }

  public async setCookie(key:string, value:string) {
    let response = await this.sendCommand('user', 'set-cookie', {key, value});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    let cookie = this.findCookie(key);
    if(cookie)
      cookie.value = value;
    else
      this.user.cookies.push({key, value});
    return {success: true};
  }

  public async deleteCookie(key:string) {
    let response = await this.sendCommand('user', 'delete-cookie', {key});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    let cookie = this.findCookie(key);
    if(cookie) {
      let i = this.user.cookies.indexOf(cookie);
      this.user.cookies.splice(i, 1);
    }
    return {success: true};
  }

  public getCookies(): any[] {
    return this.user.cookies;
  }

  public getCookie(key:string): string {
    for(let i = 0; i < this.user.cookies.length; i++)
      if(this.user.cookies[i].key == key)
        return this.user.cookies[i].value;
    return null;
  }

  // follows
  public async follow(id:string) {
    let response = await this.sendCommand('user', 'follow', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    this.user.following.push(id);
    return {success: true};
  }

  public async unfollow(id:string) {
    let response = await this.sendCommand('user', 'unfollow', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let i = this.user.following.indexOf(id);
    if(i != -1)
      this.user.following.splice(i, 1);

    return {success: true};
  }

  public isFollowing(id:string) {
    return this.user.following.indexOf(id) != -1;
  }

  protected getFollows(users:any[]) {
    let follows = [];

    for(let i = 0; i < this.user.friends.length; i++)
      follows.push(this.user.friends[i].id);

    for(let i = 0; i < users.length; i++)
      if(follows.indexOf(users[i]) == -1)
        follows.push(users[i]);
        
    return follows;
  }

  public getFollowing() {
    return this.getFollows(this.user.following);
  }

  public getFollowers() {
    return this.getFollows(this.user.followers);
  }

  // friends
  public async inviteFriend(id:string) {
    let response = await this.sendCommand('user', 'invite-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let now = new Date();

    this.user.friends.push({
      id: id, 
      date: now.toISOString(), 
      state: 'pending'
    });

    Server.network.sendCommand('invite-friend', {friend: id});

    return {success: true};
  }

  public async inviteFriendBySlug(slug:string) {
    let response = await this.sendCommand('user', 'invite-friend', {slug});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let now = new Date();
    let friendId = response.body.id;

    this.user.friends.push({
      id: friendId, 
      date: now.toISOString(), 
      state: 'pending'
    });

    Server.network.sendCommand('invite-friend', {friend: friendId});

    return {success: true};
  }

  public async removeFriend(id:string) {
    let response = await this.sendCommand('user', 'remove-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let friend = this.getFriend(id);

    if(friend) {
      let i = this.user.friends.indexOf(friend);
      if(i != -1)
        this.user.friends.splice(i, 1);
    }

    if(friend.state != 'declined')
      Server.network.sendCommand('remove-friend', {friend: id});
  
    return {success: true};
  }

  public async acceptFriend(id:string) {
    let response = await this.sendCommand('user', 'accept-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let friend = this.getFriend(id);
    if(friend)
      friend.state = 'accepted';

    Server.network.sendCommand('accept-friend', {friend: id});

    return {success: true};
  }

  public async declineFriend(id:string) {
    let response = await this.sendCommand('user', 'decline-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let friend = this.getFriend(id);
    if(friend) {
      let i = this.user.friends.indexOf(friend);
      if(i != -1)
        this.user.friends.splice(i, 1);
    }
    
    Server.network.sendCommand('decline-friend', {friend: id});

    return {success: true};
  }

  public getFriends() {
    if(!this.user)
      return [];
    return this.user.friends;
  }

  public isFriend(id:string):boolean {
    for(let i = 0; i < this.user.friends.length; i++) 
      if(this.user.friends[i].id == id)
        return (this.user.friends[i].state == 'accepted');
    return false;
  }

  public getFriend(id:string):any {
    for(let i = 0; i < this.user.friends.length; i++) 
      if(this.user.friends[i].id == id)
        return this.user.friends[i];
    return null;
  }

  public async getFriendFollows(id:string) {
    let response = await this.sendCommand('user', 'get-friend-follows', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, following: response.body.following, followers: response.body.followers};
  }

  public async getFriendsCookie(key:string) {
    let response = await this.sendCommand('user', 'get-friends-cookie', {key});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, cookies: response.body.cookies};
  }

  public async sendGift(friend:string, asset:string, quantity:number) {
    let response = await this.sendCommand('user', 'send-gift', {friend, asset, quantity});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.removeAssetsFromCache(asset, quantity);

    // Server.network.sendCommand('accept-friend', {friend: id});

    return {success: true};
  }

  // chats
  public getChats() {
    return this.user.chats;
  }

  // logging
  public async getLog() {
    let response = await this.sendCommand('user', 'get-log');
    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, log: response.body.log};
  }

  // network event handlers
  protected onFriendConnect(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend) return;

    friend.presence = {
      date: event.date,
      location: 'site',
      online: true
    };

    this.notifyFriendUpdated(event);
  }

  protected onFriendDisconnect(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend) return;

    friend.presence = {online: false};
    
    this.notifyFriendUpdated(event);
  }

  protected onFriendPresence(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend) return;

    friend.presence = {
      date: event.date,
      location: event.location,
      online: true
    };

    this.notifyFriendUpdated(event);
  }

  protected async onFriendInvited(event:any) {
    let friend = this.getFriend(event.friend);

    if(!friend) {
      friend = {
        id: event.friend,
        state: 'invited'
      };
      this.user.friends.push(friend);
    }
    else
      friend.state = 'invited';

    friend.presence = {
      online: true,
      location: 'site',
      date: event.date
    }

    await Server.public.loadProfiles([event.friend]);

    this.notifyFriendUpdated(event);
  }

  protected onFriendAccepted(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend)
      return;

    friend.state = 'accepted';

    friend.presence = {
      online: true,
      location: 'site',
      date: event.date
    }

    this.notifyFriendUpdated(event);
  }

  protected onFriendDeclined(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend)
      return;

    friend.state = 'declined';

    friend.presence = {
      online: false
    }

    this.notifyFriendUpdated(event);
  }

  protected onFriendRemoved(event:any) {
    for(let i = 0; i < this.user.friends.length; i++) {
      if(this.user.friends[i].id == event.friend) {
        this.user.friends.splice(i, 1);
        this.notifyFriendUpdated(event);
        return;
      }
    }
  }

  protected notifyFriendUpdated(event:any) {
    this.notifyListeners('friend-updated', {friend: event.friend, action: event.id.substring(7)});
  }
}
